import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Mail, Lock, User, Loader2, PawPrint } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/Button';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { signInWithEmail, signUpWithEmail, signInWithGoogle, resetPasswordForEmail } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (mode === 'login') {
        const { error } = await signInWithEmail(email, password);
        if (error) throw error;
        onClose();
      } else if (mode === 'signup') {
        const { error } = await signUpWithEmail(email, password, fullName);
        if (error) throw error;
        setSuccess(true);
      } else if (mode === 'forgot') {
        const { error } = await resetPasswordForEmail(email);
        if (error) throw error;
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-md bg-white rounded-[2rem] shadow-2xl overflow-hidden border-4 border-primary/10"
        >
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 hover:bg-black/5 rounded-full transition-colors z-10"
          >
            <X className="w-6 h-6 text-ink/40" />
          </button>

          <div className="p-8 pt-12">
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <PawPrint className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-serif text-ink">
                {success ? 'Check your inbox!' : mode === 'login' ? 'Welcome Back' : mode === 'signup' ? 'Join the Pack' : 'Reset Password'}
              </h2>
              <p className="text-ink/60 mt-2">
                {success 
                  ? "We've sent a link to your email." 
                  : mode === 'login' ? 'Your pup missed you!' : mode === 'signup' ? 'Create an account for your furry friend.' : "We'll send you a link to get back in."}
              </p>
            </div>

            {success ? (
              <div className="space-y-6 text-center">
                <div className="p-4 bg-primary/5 rounded-2xl border border-primary/10">
                  <p className="text-ink/80 text-sm">
                    {mode === 'forgot' 
                      ? "Please click the link in the email to reset your password."
                      : "Please click the link in the email to verify your account."}
                    {" "}You can close this window now.
                  </p>
                </div>
                <Button variant="primary" className="w-full" onClick={onClose}>
                  Got it!
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'signup' && (
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/30" />
                    <input
                      type="text"
                      placeholder="Full Name"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-primary/5 focus:border-primary/20 outline-none transition-colors font-sans"
                    />
                  </div>
                )}

                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/30" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-primary/5 focus:border-primary/20 outline-none transition-colors font-sans"
                  />
                </div>

                {mode !== 'forgot' && (
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/30" />
                    <input
                      type="password"
                      placeholder="Password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-primary/5 focus:border-primary/20 outline-none transition-colors font-sans"
                    />
                  </div>
                )}

                {mode === 'login' && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setMode('forgot')}
                      className="text-xs text-primary font-bold hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                {error && (
                  <p className="text-red-500 text-sm text-center font-medium bg-red-50 py-2 rounded-lg">
                    {error}
                  </p>
                )}

                <Button
                  variant="primary"
                  className="w-full py-4 text-lg shadow-lg"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  ) : (
                    mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Reset Link'
                  )}
                </Button>

                {mode !== 'forgot' && (
                  <>
                    <div className="relative py-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-ink/10"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-ink/40">Or continue with</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => { signInWithGoogle(); onClose(); }}
                      className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border-2 border-ink/5 hover:bg-black/5 transition-colors font-sans font-bold text-ink"
                    >
                      <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                      Google
                    </button>
                  </>
                )}

                <p className="text-center text-ink/60 text-sm mt-6">
                  {mode === 'login' ? "Don't have an account? " : mode === 'signup' ? "Already have an account? " : "Remembered your password? "}
                  <button
                    type="button"
                    onClick={() => {
                      if (mode === 'login') setMode('signup');
                      else setMode('login');
                      setError(null);
                    }}
                    className="text-primary font-bold hover:underline"
                  >
                    {mode === 'login' ? 'Sign Up' : 'Sign In'}
                  </button>
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
